import type {
  ISkinScan,
  ISkinScanComparison,
  ISkinScanQualityResult,
  ISkinScanShareInfo,
  SkinImageDirectionEnum,
} from '@torim-app/types';
import { adminUrlBuilder } from '../utils';

/** Error thrown when the server rejects a scan image as unusable (HTTP 422). */
export class SkinScanQualityError extends Error {
  quality?: ISkinScanQualityResult;
  constructor(message: string, quality?: ISkinScanQualityResult) {
    super(message);
    this.name = 'SkinScanQualityError';
    this.quality = quality;
  }
}

export class SkinScanApi {
  /**
   * Upload a captured face image for analysis. `formData` must contain the `image`
   * file plus the scan metadata fields (customerId, consentAcknowledged, …).
   * Throws {@link SkinScanQualityError} when the image is rejected as unusable.
   */
  static async createScan(formData: FormData): Promise<ISkinScan> {
    const url = adminUrlBuilder({}, 'skin-scan');
    const response = await fetch(url, { method: 'post', body: formData });

    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      if (response.status === 422) {
        throw new SkinScanQualityError(body?.error || 'התמונה אינה מתאימה לסריקה', body?.quality);
      }
      throw new Error(body?.error || 'הסריקה נכשלה, נסו שוב');
    }
    return await response.json();
  }

  static async fetchScans(customerId: string): Promise<ISkinScan[]> {
    const url = adminUrlBuilder({ customerId }, 'skin-scan/list');
    const response = await fetch(url, {
      method: 'get',
      headers: { 'content-type': 'application/json' },
    });
    if (!response.ok) throw 'לא הצלחנו לטעון את הסריקות';
    return await response.json();
  }

  static async getScan(scanId: string): Promise<ISkinScan> {
    const url = adminUrlBuilder({ scanId }, 'skin-scan/scan');
    const response = await fetch(url, {
      method: 'get',
      headers: { 'content-type': 'application/json' },
    });
    if (!response.ok) throw 'לא הצלחנו לטעון את הסריקה';
    return await response.json();
  }

  static async compareScans(scanIdA: string, scanIdB: string): Promise<ISkinScanComparison> {
    const url = adminUrlBuilder({ scanIdA, scanIdB }, 'skin-scan/compare');
    const response = await fetch(url, {
      method: 'get',
      headers: { 'content-type': 'application/json' },
    });
    if (!response.ok) throw 'לא הצלחנו להשוות את הסריקות';
    return await response.json();
  }

  static async updateNote(scanId: string, notes: string): Promise<ISkinScan> {
    const url = adminUrlBuilder({}, 'skin-scan/note');
    const response = await fetch(url, {
      method: 'post',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ scanId, notes }),
    });
    if (!response.ok) throw 'לא הצלחנו לשמור את ההערה';
    return await response.json();
  }

  static async getTreatmentPlan(customerId: string): Promise<{ treatmentPlan: string }> {
    const url = adminUrlBuilder({ customerId }, 'skin-scan/treatment-plan');
    const response = await fetch(url, { method: 'get', headers: { 'content-type': 'application/json' } });
    if (!response.ok) throw 'לא הצלחנו לטעון את תוכנית הטיפול';
    return await response.json();
  }

  static async setTreatmentPlan(customerId: string, treatmentPlan: string): Promise<{ treatmentPlan: string }> {
    const url = adminUrlBuilder({}, 'skin-scan/treatment-plan');
    const response = await fetch(url, {
      method: 'post',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ customerId, treatmentPlan }),
    });
    if (!response.ok) throw 'לא הצלחנו לשמור את תוכנית הטיפול';
    return await response.json();
  }

  static async getRequestedDirections(customerId: string): Promise<{ directions: SkinImageDirectionEnum[] }> {
    const url = adminUrlBuilder({ customerId }, 'skin-scan/directions');
    const response = await fetch(url, { method: 'get', headers: { 'content-type': 'application/json' } });
    if (!response.ok) throw 'לא הצלחנו לטעון את התמונות הנדרשות';
    return await response.json();
  }

  static async setRequestedDirections(
    customerId: string,
    directions: SkinImageDirectionEnum[],
  ): Promise<{ directions: SkinImageDirectionEnum[] }> {
    const url = adminUrlBuilder({}, 'skin-scan/directions');
    const response = await fetch(url, {
      method: 'post',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ customerId, directions }),
    });
    if (!response.ok) throw 'לא הצלחנו לשמור את התמונות הנדרשות';
    return await response.json();
  }

  static async getTracking(customerId: string): Promise<{ active: boolean; frequencyDays?: number }> {
    const url = adminUrlBuilder({ customerId }, 'skin-scan/tracking');
    const response = await fetch(url, { method: 'get', headers: { 'content-type': 'application/json' } });
    if (!response.ok) throw 'לא הצלחנו לטעון את מצב המעקב';
    return await response.json();
  }

  static async setTracking(
    customerId: string,
    active: boolean,
    frequencyDays?: number,
  ): Promise<{ active: boolean; frequencyDays?: number }> {
    const url = adminUrlBuilder({}, 'skin-scan/tracking');
    const response = await fetch(url, {
      method: 'post',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ customerId, active, frequencyDays }),
    });
    if (!response.ok) throw 'לא הצלחנו לעדכן את המעקב';
    return await response.json();
  }

  static async getShareInfo(customerId: string): Promise<ISkinScanShareInfo> {
    const url = adminUrlBuilder({ customerId }, 'skin-scan/share');
    const response = await fetch(url, { method: 'get', headers: { 'content-type': 'application/json' } });
    if (!response.ok) throw 'לא הצלחנו לטעון את מצב השיתוף';
    return await response.json();
  }

  static async setShare(customerId: string, enabled: boolean): Promise<ISkinScanShareInfo> {
    const url = adminUrlBuilder({}, 'skin-scan/share');
    const response = await fetch(url, {
      method: 'post',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ customerId, enabled }),
    });
    if (!response.ok) throw 'לא הצלחנו לעדכן את השיתוף';
    return await response.json();
  }

  static async deleteScan(scanId: string): Promise<void> {
    const url = adminUrlBuilder({ scanId }, 'skin-scan/scan');
    const response = await fetch(url, {
      method: 'delete',
      headers: { 'content-type': 'application/json' },
    });
    if (!response.ok) throw 'לא הצלחנו למחוק את הסריקה';
  }
}
